import { CSSResultGroup, html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { validate } from 'typia'
import { validationError } from 'components/discovery/shared/validation.js'
import { tierPricingStyle } from './tier-pricing.style.js'
import { PdpActionsContext, PdpQuantityContext, type PdpActionsContextType, type PdpQuantityContextType } from 'components/discovery/pdp/provider.js'
import type { TierPricing, TierPricingSingle } from 'components/discovery/pdp/tier-pricing/typing/tier-pricing-typing.js'
import { productPriceTiersPath } from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import { tierPricingApi, GetTierPricingRequest } from 'components/discovery/pdp/tier-pricing/api/tier-pricing-api.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'

export const tierPricingHiddenFields = ['discount'] as const

@customElement('csc-tier-pricing')
export class CSCTierPricing extends HiddenFieldsMixin(LitElement, tierPricingHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, tierPricingStyle]

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  @consume({ context: PdpQuantityContext, subscribe: true })
  @state()
  private quantity: PdpQuantityContextType = 0

  @consume({ context: PdpActionsContext })
  private actions!: PdpActionsContextType

  @state()
  private tiers: TierPricing = []

  @state()
  private activeIndex = -1

  private tiersFetched: Promise<void> = Promise.resolve()

  private previousProductId: number | null = null

  private get hasOwnEndpointSource(): boolean {
    return this.productId !== null
  }

  override connectedCallback() {
    super.connectedCallback()

    this.previousProductId = this.productId

    if (this.hasOwnEndpointSource) {
      this.tiersFetched = this.fetchOwnTiers()
    }
  }

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)

    if (!changedProperties.has('productId')) return
    if (this.previousProductId === this.productId) return

    this.previousProductId = this.productId

    if (this.hasOwnEndpointSource) {
      this.tiersFetched = this.fetchOwnTiers()
    } else {
      this.tiers = []
    }
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete()
    await this.tiersFetched
    return result
  }

  private async fetchOwnTiers() {
    if (this.productId === null) return
    const endpointUrl = (await createUrl(productPriceTiersPath(this.productId))).href
    await this.fetchTiers(endpointUrl)
  }

  private async fetchTiers(endpointUrl: string) {
    if (!this.isConnected) return

    try {
      const response = await tierPricingApi.getTiers(new GetTierPricingRequest(endpointUrl))
      if (!this.isConnected) return

      const tiersValidation = validate<TierPricing>(response)
      if (!tiersValidation.success) {
        validationError('TierPricing', tiersValidation.errors)
        return
      }

      this.tiers = tiersValidation.data
    } catch (error) {
      if (!this.isConnected) return
      console.error('<csc-tier-pricing> failed to fetch price tiers', error)
    }
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('quantity') || changedProperties.has('tiers')) {
      this.syncActiveIndex()
    }
  }

  private syncActiveIndex() {
    let best = -1
    for (let i = 0; i < this.tiers.length; i++) {
      const tier = this.tiers[i]
      if (!tier) continue
      const tierQty = parseInt(tier.quantity, 10)
      if (this.quantity >= tierQty) {
        best = i
      }
    }
    if (best === this.activeIndex) return
    this.activeIndex = best
    const tier = best !== -1 ? this.tiers[best] ?? null : null
    if (this.actions) {
      this.actions.setTier(tier)
      this.dispatchEvent(new CustomEvent('csc-tier-changed', { bubbles: true, composed: true, detail: { tier } }))
    }
  }

  private selectTier(index: number) {
    const tier = this.tiers[index]
    if (!tier) return
    this.activeIndex = index
    this.actions.setTier(tier)
    this.actions.setQuantity(parseInt(tier.quantity, 10))
    this.dispatchEvent(new CustomEvent('csc-tier-changed', { bubbles: true, composed: true, detail: { tier } }))
  }

  private renderTier = (tier: TierPricingSingle, index: number): TemplateResult => {
    return html`
      <cse-selectable
        fit="fill"
        state="${this.activeIndex === index ? 'selected' : 'selectable'}"
        @click="${() => this.selectTier(index)}"
      >
        <div class="tier">
          <cse-text class="tier-quantity" variant="body-s">${t('pieces', { ns: 'csc-tier-pricing', count: parseInt(tier.quantity, 10), default: '{{count}} piece', default_plural: '{{count}} pieces' })}</cse-text>
          <cse-divider orientation="vertical" variant="solid"></cse-divider>
          <div class="tier-price-info">
            <cse-price class="tier-price" size="s">${tier.price}</cse-price>
            ${this.isHidden('discount') ? nothing : html`
              <cse-badge class="tier-discount" variant="sale" composition="simple">
                ${tier.discount} ${t('discount', { ns: 'csc-tier-pricing', default: 'discount' })}
              </cse-badge>`}
          </div>
        </div>
      </cse-selectable>
    `
  }

  override render() {
    return html`
      <div class="tier-list">
        ${this.tiers.map((tier, index) => this.renderTier(tier, index))}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tier-pricing': CSCTierPricing
  }
}
